import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../lib/Seo";
import RevealText from "../lib/RevealText";
import Reveal from "../lib/Reveal";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const galleries = [
  {
    slug: "tigers",
    number: "01",
    title: "Tigers",
    subtitle: "The stripes of the subcontinent",
    intro:
      "Bengal & royal white — captured ground-level, at first light, from a distance measured in patience rather than metres.",
    frames: [
      {
        src: "https://images.unsplash.com/photo-1561731216-c3a4d99437d5?auto=format&fit=crop&w=2400&q=80",
        caption: "Bengal · Ranthambhore",
        format: "wide",
      },
      {
        src: "https://images.unsplash.com/photo-1602491453631-e2a5ad90a131?auto=format&fit=crop&w=1800&q=80",
        caption: "White tiger · Nagarhole",
        format: "tall",
      },
    ],
  },
  {
    slug: "elephants",
    number: "02",
    title: "Elephants",
    subtitle: "Matriarchs of the plains",
    intro:
      "Frames composed at eye-level, from the low hide — a photographer's respect for the largest walking mind on the continent.",
    frames: [
      {
        src: "https://images.unsplash.com/photo-1557050543-4d5f4e07ef46?auto=format&fit=crop&w=1800&q=80",
        caption: "Tusker · Amboseli",
        format: "tall",
      },
      {
        src: "https://images.unsplash.com/photo-1564760055775-d63b17a55c44?auto=format&fit=crop&w=2400&q=80",
        caption: "Herd at rose hour · Serengeti",
        format: "wide",
      },
    ],
  },
  {
    slug: "polar",
    number: "03",
    title: "Polar Bears",
    subtitle: "The white north",
    intro:
      "A departure from the plains — the same discipline, applied to the ice. Patience, respect, and the slow reveal of the wild.",
    frames: [
      {
        src: "https://upload.wikimedia.org/wikipedia/commons/6/66/Polar_Bear_-_Alaska_%28cropped%29.jpg",
        caption: "Alaska · February",
        format: "wide",
      },
      {
        src: "https://images.unsplash.com/photo-1589656966895-2f33e7653819?auto=format&fit=crop&w=1800&q=80",
        caption: "Cub · Wapusk",
        format: "tall",
      },
    ],
  },
];

export default function LensOfJeetAman() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <>
      <Seo
        title="Through the Lens of Jeet Aman | Njia Safaris"
        description="A cinematic wildlife gallery — tigers, elephants and polar bears captured by Jeet Aman, the mentor and creative visionary behind Njia Safaris."
        image="https://images.unsplash.com/photo-1561731216-c3a4d99437d5?auto=format&fit=crop&w=1600&q=80"
        path="/lens-of-jeet-aman"
      />
      <Nav />

      {/* HERO — cinematic dark */}
      <section
        ref={heroRef}
        className="relative h-[100svh] overflow-hidden section-dark"
        data-testid="lens-hero"
      >
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1561731216-c3a4d99437d5?auto=format&fit=crop&w=2400&q=85"
            alt="Bengal tiger portrait"
            className="w-full h-full object-cover"
            style={{ objectPosition: "50% 25%" }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/40 to-black/80" />
        <div className="grain-overlay" />

        <div className="relative z-10 h-full container-x flex flex-col justify-end pb-16 md:pb-24">
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-16 bg-[color:var(--gold)]" />
            <span className="eyebrow eyebrow-invert">The lens · A gallery</span>
          </div>
          <RevealText
            as="h1"
            className="h-display text-[12vw] md:text-[8vw] text-[color:var(--ivory)] leading-[0.95]"
            lines={["Through the lens", <em key="e" className="italic text-[color:var(--gold-3)]">of Jeet Aman.</em>]}
            stagger={110}
          />
          <p className="mt-8 font-italic-display text-2xl md:text-3xl text-[color:var(--sand)] max-w-3xl">
            Frames from the field — tigers, elephants and the white north.
          </p>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="section-dark py-24 md:py-32 border-t border-[color:var(--line-invert)]" data-testid="lens-quote">
        <div className="container-x grid md:grid-cols-12 gap-10">
          <div className="md:col-span-2">
            <span
              className="font-display italic text-8xl leading-none"
              style={{ color: "var(--gold)" }}
              aria-hidden
            >
              &ldquo;
            </span>
          </div>
          <div className="md:col-span-9">
            <RevealText
              as="p"
              className="font-italic-display text-3xl md:text-5xl leading-[1.15] text-[color:var(--ivory)] max-w-4xl"
              lines={[
                "Every frame is a story of patience,",
                "respect, and the raw beauty of the wild.",
              ]}
              stagger={120}
            />
            <p className="mt-8 mono text-[11px] uppercase tracking-[0.28em] text-[color:var(--gold-3)]">
              — Jeet Aman
            </p>
          </div>
        </div>
      </section>

      {/* GALLERIES */}
      {galleries.map((g, gi) => (
        <section
          key={g.slug}
          className={gi % 2 === 0 ? "section-dark" : "section-forest"}
          data-testid={`lens-gallery-${g.slug}`}
        >
          <div className="container-x pt-24 md:pt-32 pb-6">
            <div className="grid md:grid-cols-12 gap-10 mb-14">
              <div className="md:col-span-4">
                <span className="chapter-num">{g.number}</span>
                <p className="eyebrow eyebrow-invert mt-2">{g.subtitle}</p>
              </div>
              <div className="md:col-span-7 md:col-start-6">
                <RevealText
                  as="h2"
                  className="h-display text-5xl md:text-6xl text-[color:var(--ivory)]"
                  lines={[g.title]}
                />
                <p className="mt-6 text-lg leading-relaxed text-[color:var(--sand)] max-w-xl">
                  {g.intro}
                </p>
              </div>
            </div>
          </div>

          {/* Frames — editorial layout */}
          <div className="container-x pb-24 md:pb-32">
            <div className="grid grid-cols-12 gap-6 md:gap-10">
              {g.frames.map((f, i) => {
                const wide = f.format === "wide";
                const alt = i === 1;
                return (
                  <Reveal
                    key={i}
                    delay={i * 0.08}
                    className={
                      wide
                        ? alt
                          ? "col-span-12 md:col-span-9 md:col-start-4"
                          : "col-span-12 md:col-span-10"
                        : alt
                          ? "col-span-9 col-start-4 md:col-span-5 md:col-start-8"
                          : "col-span-10 md:col-span-6"
                    }
                  >
                    <div
                      className={`img-frame ${wide ? "aspect-[16/9]" : "aspect-[3/4]"} shadow-editorial`}
                    >
                      <img src={f.src} alt={f.caption} loading="lazy" />
                    </div>
                    <p className="mt-4 mono text-[11px] uppercase tracking-[0.28em] text-[color:var(--sand)]">
                      {String(gi + 1).padStart(2, "0")}.{String(i + 1).padStart(2, "0")} · {f.caption}
                    </p>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* CLOSING */}
      <section className="section-dark py-24 md:py-32 border-t border-[color:var(--line-invert)]" data-testid="lens-closing">
        <div className="container-x grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="eyebrow eyebrow-invert mb-6">The mentor</p>
            <RevealText
              as="h2"
              className="h-display text-5xl md:text-6xl text-[color:var(--ivory)]"
              lines={["Njia was composed", <em key="e" className="italic text-[color:var(--gold-3)]">from these frames.</em>]}
            />
          </div>
          <div className="md:col-span-5">
            <p className="text-lg leading-relaxed text-[color:var(--sand)] max-w-md mb-8">
              Jeet Aman&rsquo;s cinematic, ground-level approach to wildlife photography inspired the ethos of Njia Safaris:
              travel that waits, listens, and honours the wild before it reaches for the shutter.
            </p>
            <Link to="/about" className="btn-ghost btn-ghost-invert" data-testid="lens-about-link">
              Read the philosophy
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
